<template>
  <!-- Moved to TopNavBar popover — this floating widget is now disabled -->
  <div v-if="false" class="spb-float" @mouseenter="expanded=true" @mouseleave="expanded=false">

    <!-- Collapsed pill button -->
    <button class="spb-pill" :class="{ 'spb-pill--done': pct >= 100 }" @click="goSetup">
      <svg class="spb-ring" viewBox="0 0 36 36">
        <circle class="spb-ring-bg" cx="18" cy="18" r="15.9" />
        <circle class="spb-ring-fill" :class="{ 'spb-ring-fill--done': pct >= 100 }"
                cx="18" cy="18" r="15.9"
                :stroke-dasharray="pct + ' 100'" />
      </svg>
      <span class="spb-pill-pct">{{ pct }}%</span>
      <span class="spb-pill-label">Setup</span>
      <button class="spb-pill-x" @click.stop="dismiss" title="Dismiss">&times;</button>
    </button>

    <!-- Expanded popover on hover -->
    <transition name="spb-pop">
      <div v-if="expanded" class="spb-popover">
        <div class="spb-pop-header">
          <span class="spb-pop-icon">{{ pct >= 100 ? '✅' : '🚀' }}</span>
          <div>
            <div class="spb-pop-title">{{ pct >= 100 ? 'Setup Complete!' : 'Setup in progress' }}</div>
            <div class="spb-pop-sub">{{ doneTasks }}/{{ totalTasks }} tasks done</div>
          </div>
        </div>
        <div class="spb-pop-track">
          <div class="spb-pop-fill" :style="{ width: pct + '%' }"></div>
        </div>
        <button class="spb-pop-btn" @click="goSetup">
          {{ pct >= 100 ? 'View Setup' : 'Continue Setup' }} &rarr;
        </button>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "SetupProgressBar",
  data() {
    return {
      visible: false,
      dismissed: false,
      expanded: false,
      loading: false,
      phases: [],
      progress: {},
      phasesDone: {},
      entityId: parseInt(localStorage.getItem("ENTITY_ID")) || 0,
      pollTimer: null,
    };
  },
  computed: {
    totalPhases() {
      return this.phases.length || 1;
    },
    totalTasks() {
      return this.phases.reduce((sum, p) => sum + (p.tasks ? p.tasks.length : 0), 0);
    },
    doneTasks() {
      let count = 0;
      this.phases.forEach((p, pi) => {
        if (p.tasks) {
          p.tasks.forEach((_, ti) => {
            if (this.progress[pi] && this.progress[pi][ti]) count++;
          });
        }
      });
      return count;
    },
    pct() {
      return this.totalTasks ? Math.round((this.doneTasks / this.totalTasks) * 100) : 0;
    },
    currentPhase() {
      const idx = this.phases.findIndex((_, pi) => !this.phasesDone[pi]);
      return idx >= 0 ? idx : Math.max(this.phases.length - 1, 0);
    },
  },
  methods: {
    async loadData() {
      if (!this.entityId || this.dismissed) return;
      try {
        const [phasesRes, progressRes] = await Promise.all([
          axios.get("setup_guide/tasks"),
          axios.get("setup_guide/progress", { params: { entity_id: this.entityId } }),
        ]);

        this.phases = (phasesRes.data.data || []).map(p => ({
          tasks: p.tasks || [],
        }));

        const records = progressRes.data.data || [];
        const autoMap = progressRes.data.auto_detected || {};
        const prog = {};
        const done = {};
        records.forEach(r => {
          if (r.task_index === -1) {
            if (r.phase_completed) done[r.phase_index] = true;
          } else {
            if (!prog[r.phase_index]) prog[r.phase_index] = {};
            prog[r.phase_index][r.task_index] = r.done;
          }
        });

        // Merge auto-detected tasks
        this.phases.forEach((p, pi) => {
          (p.tasks || []).forEach((t, ti) => {
            const key = t.component_key;
            if (key && autoMap[key]) {
              if (!prog[pi]) prog[pi] = {};
              prog[pi][ti] = true;
            }
          });
        });

        // Auto-complete phases where all tasks are done
        this.phases.forEach((p, pi) => {
          if (!done[pi] && p.tasks && p.tasks.length > 0) {
            const allDone = p.tasks.every((_, ti) => prog[pi] && prog[pi][ti]);
            if (allDone) done[pi] = true;
          }
        });

        this.progress = prog;
        this.phasesDone = done;

        // Show only if setup is incomplete and not on setup_guide page
        this.visible = this.pct < 100 && this.$route.path !== "/setup_guide";
      } catch (err) {
        console.warn("SetupProgressBar: could not load progress", err.message);
        this.visible = false;
      }
    },
    goSetup() {
      this.$router.push("/setup_guide");
    },
    dismiss() {
      this.dismissed = true;
      this.visible = false;
    },
  },
  watch: {
    "$route.path"(newPath, oldPath) {
      if (this.dismissed) return;
      if (newPath === "/setup_guide") {
        this.visible = false;
      } else {
        if (oldPath === "/setup_guide") {
          this.loadData();
        } else if (this.phases.length && this.pct < 100) {
          this.visible = true;
        }
      }
    },
  },
  mounted() {
    this.loadData();
    this.pollTimer = setInterval(() => {
      if (!this.dismissed && this.$route.path !== "/setup_guide") {
        this.loadData();
      }
    }, 120000);
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
};
</script>

<style scoped>
/* ── Float container (bottom-right) ─────────────── */
.spb-float {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
}

/* ── Pill button ────────────────────────────────── */
.spb-pill {
  display: flex;
  align-items: center;
  gap: 5px;
  background: #1A1A2E;
  color: #fff;
  border: none;
  border-radius: 50px;
  padding: 5px 10px 5px 5px;
  cursor: pointer;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s, box-shadow 0.2s;
}
.spb-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
}
.spb-pill--done {
  background: #065F46;
}

/* Circular progress ring */
.spb-ring {
  width: 22px;
  height: 22px;
  transform: rotate(-90deg);
  flex-shrink: 0;
}
.spb-ring-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.12);
  stroke-width: 3;
}
.spb-ring-fill {
  fill: none;
  stroke: #6366F1;
  stroke-width: 3;
  stroke-linecap: round;
  transition: stroke-dasharray 0.6s ease;
}
.spb-ring-fill--done {
  stroke: #10B981;
}

.spb-pill-pct {
  font-size: 10px;
  font-weight: 800;
  min-width: 20px;
}
.spb-pill-label {
  font-size: 9px;
  font-weight: 600;
  opacity: 0.8;
  letter-spacing: 0.3px;
}
.spb-pill-x {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.35);
  font-size: 13px;
  cursor: pointer;
  padding: 0 1px;
  margin-left: 1px;
  line-height: 1;
}
.spb-pill-x:hover {
  color: #fff;
}

/* ── Popover (on hover) ────────────────────────── */
.spb-popover {
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  background: #fff;
  border-radius: 10px;
  padding: 10px 12px;
  width: 200px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.14);
  border: 1px solid #E6E4EE;
  color: #1E1E2F;
}
.spb-popover::after {
  content: '';
  position: absolute;
  bottom: -5px;
  right: 18px;
  width: 10px;
  height: 10px;
  background: #fff;
  border-right: 1px solid #E6E4EE;
  border-bottom: 1px solid #E6E4EE;
  transform: rotate(45deg);
}

.spb-pop-header {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 8px;
}
.spb-pop-icon {
  font-size: 16px;
}
.spb-pop-title {
  font-size: 11px;
  font-weight: 800;
  color: #1E1E2F;
}
.spb-pop-sub {
  font-size: 9px;
  color: #9A96A8;
  margin-top: 1px;
}

.spb-pop-track {
  height: 4px;
  background: #EEEDF4;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 5px;
}
.spb-pop-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366F1, #10B981);
  border-radius: 2px;
  transition: width 0.6s ease;
}

.spb-pop-phase {
  font-size: 9px;
  color: #9A96A8;
  margin-bottom: 8px;
}

.spb-pop-btn {
  display: block;
  width: 100%;
  background: #1A1A2E;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 5px 0;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
  text-align: center;
}
.spb-pop-btn:hover {
  background: #2D2B55;
}

/* ── Popover transition ─────────────────────────── */
.spb-pop-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.spb-pop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.spb-pop-enter, .spb-pop-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* ── Mobile ─────────────────────────────────────── */
@media (max-width: 576px) {
  .spb-float {
    bottom: 16px;
    right: 16px;
  }
  .spb-pill-label {
    display: none;
  }
  .spb-popover {
    width: 180px;
  }
}
</style>
